import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ResolvedImage } from "../media/ResolvedImage";

// Sovereign Emerald palette — mirrors DynamicSidebar.tsx
const SE = {
  paper:         '#F2EADB',
  raised:        'rgba(250,247,238,0.98)',
  hairline:      'rgba(11,59,46,0.14)',
  hairlineSoft:  'rgba(11,59,46,0.12)',
  ink:           '#2A1F17',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  brand:         '#339274',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  hoverWash:     'rgba(11,59,46,0.05)',
  muted:         '#7C8A82',
  danger:        '#A03030',
  dangerWash:    'rgba(160,48,48,0.07)',
};

export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, role, logout } = useAuth();
  const navigate = useNavigate();
  const panelRef  = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const handleLogout = async () => {
    try { await logout(); } catch { logout(); }
    navigate("/signin");
  };

  const getDisplayName  = () => user?.name  || user?.email || "User";
  const getContact      = () => user?.email || user?.mobile || "";
  const getFormattedRole = () => {
    if (!role) return "";
    return role.split("_").map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
  };
  const getInitials = () => {
    const n = user?.name || user?.email || "U";
    const parts = n.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : n.charAt(0).toUpperCase();
  };

  // Reusable avatar
  const Avatar = ({ size }: { size: number }) => (
    <span
      className="relative flex-shrink-0 flex items-center justify-center rounded-full text-white font-bold tracking-tight overflow-hidden"
      style={{
        width: size,
        height: size,
        fontSize: size > 38 ? 15 : 12,
        backgroundImage: 'linear-gradient(135deg, #1F5A47, #0B3B2E)',
        boxShadow: `inset 0 0 0 1.5px ${SE.champagneRing}`,
      }}
    >
      {user?.profilePhoto ? (
        <ResolvedImage
          mediaSrc={user.profilePhoto}
          alt={getDisplayName()}
          className="w-full h-full object-cover"
        />
      ) : getInitials()}
      {/* Online dot */}
      <span
        className="absolute -bottom-px -right-px rounded-full"
        style={{
          width: size > 38 ? 11 : 9,
          height: size > 38 ? 11 : 9,
          backgroundColor: SE.brand,
          boxShadow: `0 0 0 2px ${SE.paper}`,
        }}
      />
    </span>
  );

  return (
    <div className="relative">
      {/* ── TRIGGER ──────────────────────────────────────────────────── */}
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(v => !v)}
        className="group flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full transition-all duration-200 focus:outline-none"
        style={{
          backgroundColor: isOpen ? SE.raised : SE.paper,
          border: `1px solid ${isOpen ? SE.champagneRing : SE.hairline}`,
          boxShadow: isOpen
            ? `0 0 0 3px ${SE.champagneWash}, 0 2px 12px rgba(11,59,46,0.10)`
            : '0 1px 3px rgba(11,59,46,0.06)',
        }}
        onMouseEnter={e => {
          if (!isOpen) {
            (e.currentTarget as HTMLElement).style.backgroundColor = SE.raised;
            (e.currentTarget as HTMLElement).style.borderColor = SE.champagneRing;
          }
        }}
        onMouseLeave={e => {
          if (!isOpen) {
            (e.currentTarget as HTMLElement).style.backgroundColor = SE.paper;
            (e.currentTarget as HTMLElement).style.borderColor = SE.hairline;
          }
        }}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Avatar size={34} />

        {/* Name + role text */}
        <span className="hidden sm:flex flex-col items-start min-w-0 leading-tight">
          <span
            className="text-[12.5px] font-semibold truncate max-w-[130px] tracking-tight"
            style={{ color: SE.title }}
          >
            {getDisplayName()}
          </span>
          {role && (
            <span
              className="text-[10.5px] font-medium truncate max-w-[130px] tracking-[0.01em]"
              style={{ color: SE.muted }}
            >
              {getFormattedRole()}
            </span>
          )}
        </span>

        {/* Chevron */}
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          className="flex-shrink-0 transition-transform duration-200"
          aria-hidden
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            color: isOpen ? SE.champagne : SE.muted,
          }}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor" strokeWidth="1.5"
            strokeLinecap="round" strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* ── DROPDOWN PANEL ───────────────────────────────────────────── */}
      {isOpen && (
        <div
          ref={panelRef}
          className="absolute right-0 z-50 flex flex-col w-[276px] rounded-2xl overflow-hidden"
          style={{
            marginTop: 10,
            backgroundColor: SE.paper,
            border: `1px solid ${SE.hairline}`,
            boxShadow:
              '1px 0 0 rgba(201,169,97,0.06), 0 4px 16px rgba(11,59,46,0.10), 0 12px 40px rgba(11,59,46,0.12)',
            animation: 'se-dropdown-in 140ms cubic-bezier(0.16,1,0.3,1) both',
          }}
        >
          <style>{`
            @keyframes se-dropdown-in {
              from { opacity: 0; transform: translateY(-6px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0)   scale(1);    }
            }
          `}</style>

          {/* ── USER IDENTITY HEADER ─────────────────────────────────── */}
          <div
            className="px-4 pt-4 pb-3.5"
            style={{
              borderBottom: `1px dashed ${SE.hairlineSoft}`,
              background: `linear-gradient(160deg, rgba(250,247,238,0.9) 0%, transparent 100%)`,
            }}
          >
            {/* Avatar + name row */}
            <div className="flex items-center gap-3 mb-2.5">
              <Avatar size={44} />
              <div className="flex-1 min-w-0">
                <p
                  className="text-[13.5px] font-bold truncate tracking-tight leading-snug"
                  style={{ color: SE.title }}
                >
                  {getDisplayName()}
                </p>
                {getContact() && (
                  <p
                    className="text-[11px] truncate mt-0.5 tracking-tight"
                    style={{ color: SE.muted }}
                  >
                    {getContact()}
                  </p>
                )}
              </div>
            </div>

            {/* Role badge */}
            {role && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold tracking-[0.12em] uppercase"
                style={{
                  backgroundColor: SE.champagneWash,
                  color: SE.bottle,
                  border: `1px solid ${SE.champagneRing}`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: SE.champagne }}
                />
                {getFormattedRole()}
              </span>
            )}
          </div>

          {/* ── MENU ITEMS ───────────────────────────────────────────── */}
          <ul
            className="px-2 py-2"
            style={{ borderBottom: `1px dashed ${SE.hairlineSoft}` }}
          >
            <li>
              <Link
                to="/profile"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[12.5px] font-medium tracking-tight transition-all duration-150"
                style={{ color: SE.ink }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = SE.hoverWash;
                  (e.currentTarget as HTMLElement).style.color = SE.title;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = SE.ink;
                }}
              >
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: 'rgba(11,59,46,0.07)', color: SE.bottle }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      fillRule="evenodd" clipRule="evenodd"
                      d="M12 3.5C7.305 3.5 3.5 7.305 3.5 12c0 2.153.8 4.119 2.12 5.616.553-2.307 2.629-4.021 5.105-4.021h2.55c2.476 0 4.552 1.714 5.105 4.021A8.473 8.473 0 0020.5 12C20.5 7.305 16.695 3.5 12 3.5zm5.025 15.357v-.011A3.756 3.756 0 0013.275 15.1h-2.55a3.756 3.756 0 00-3.75 3.746v.01A9.46 9.46 0 0012 20.5a9.46 9.46 0 005.025-1.643zM2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12zm10-4.75a2.018 2.018 0 100 4.036A2.018 2.018 0 0012 7.25z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                View Profile
              </Link>
            </li>
          </ul>

          {/* ── SIGN OUT ─────────────────────────────────────────────── */}
          <div className="px-2 py-2">
            <button
              onClick={handleLogout}
              className="group/signout flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-[12.5px] font-medium tracking-tight transition-all duration-150"
              style={{ color: SE.danger }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = SE.dangerWash;
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              <span
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
                style={{ backgroundColor: 'rgba(160,48,48,0.08)', color: SE.danger }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    fillRule="evenodd" clipRule="evenodd"
                    d="M15.1 4.75a.25.25 0 00-.25.25v4.745h-1.5V5a1.75 1.75 0 011.75-1.75h3.4A1.75 1.75 0 0120.25 5v14a1.75 1.75 0 01-1.75 1.75h-3.4A1.75 1.75 0 0113.35 19v-4.745h1.5V19a.25.25 0 00.25.25h3.4a.25.25 0 00.25-.25V5a.25.25 0 00-.25-.25H15.1zm-11.35 7.5c0-.414.336-.75.75-.75h10.185l-3.345-3.345a.75.75 0 111.06-1.06l4.608 4.607a.747.747 0 010 1.096L12.4 17.405a.75.75 0 11-1.06-1.06L14.685 13H4.5a.75.75 0 01-.75-.75z"
                    fill="currentColor"
                  />
                </svg>
              </span>
              Sign Out
            </button>
          </div>

          {/* ── CHAMPAGNE BOTTOM HAIRLINE ─────────────────────────────── */}
          <div
            className="h-px mx-4 mb-0"
            style={{
              background: `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)`,
            }}
          />
        </div>
      )}
    </div>
  );
}
